import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import {
  getStoreConnectionInvoice,
  saveStoreConnectionInvoice,
  previewStoreConnectionInvoice,
  resizeLogoToDataUrl,
  DEFAULT_INVOICE_TEMPLATE,
  type InvoiceTemplate,
} from '../lib/store';
import { Button } from '../components/ui/Button';
import Toggle from '../components/ui/Toggle';
import { ChevronLeftIcon } from '../components/icons';

function base64ToBlobUrl(base64: string): string {
  const binaryString = atob(base64);
  const len = binaryString.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  const blob = new Blob([bytes], { type: 'application/pdf' });
  return URL.createObjectURL(blob);
}

export default function InvoiceTemplateEditor() {
  const { t, dir } = useI18n();
  const isRtl = dir === 'rtl';
  const navigate = useNavigate();
  const { connectionId } = useParams<{ connectionId: string }>();

  const [template, setTemplate] = useState<InvoiceTemplate>(DEFAULT_INVOICE_TEMPLATE);
  const [initialTemplate, setInitialTemplate] = useState<InvoiceTemplate>(DEFAULT_INVOICE_TEMPLATE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [logoLoading, setLogoLoading] = useState(false);

  // Active Blob URL
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const activeBlobUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Track if user changed anything
  const isDirty = useMemo(() => {
    return JSON.stringify(template) !== JSON.stringify(initialTemplate);
  }, [template, initialTemplate]);

  // Cleanup blob URL
  const updateBlobUrl = (newUrl: string | null) => {
    if (activeBlobUrlRef.current) {
      URL.revokeObjectURL(activeBlobUrlRef.current);
    }
    activeBlobUrlRef.current = newUrl;
    setPdfBlobUrl(newUrl);
  };

  useEffect(() => {
    return () => {
      if (activeBlobUrlRef.current) {
        URL.revokeObjectURL(activeBlobUrlRef.current);
      }
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
    };
  }, []);

  // Fetch initial template
  useEffect(() => {
    if (!connectionId) {
      setError(t('invoice.invalidConnection'));
      setLoading(false);
      return;
    }

    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const inv = await getStoreConnectionInvoice(connectionId);
        if (mounted) {
          const merged = { ...DEFAULT_INVOICE_TEMPLATE, ...inv };
          setTemplate(merged);
          setInitialTemplate(merged);
        }
      } catch (err: any) {
        if (mounted) {
          setError(err.message || t('invoice.loadError'));
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [connectionId]);

  // Request preview
  const fetchPreview = async (inv: InvoiceTemplate) => {
    setRendering(true);
    setPreviewError(null);
    try {
      const res = await previewStoreConnectionInvoice(inv);
      if (res.pdfBase64) {
        const newUrl = base64ToBlobUrl(res.pdfBase64);
        updateBlobUrl(newUrl);
      }
    } catch (err: any) {
      setPreviewError(err.message || t('invoice.previewError'));
    } finally {
      setRendering(false);
    }
  };

  // Debounce preview 800ms whenever template changes
  useEffect(() => {
    if (loading) return;

    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
    }

    previewTimerRef.current = setTimeout(() => {
      void fetchPreview(template);
    }, 800);

    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
      }
    };
  }, [template, loading]);

  const handleSave = async () => {
    if (!connectionId || !isDirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      const res = await saveStoreConnectionInvoice(connectionId, template);
      setTemplate(res.invoice);
      setInitialTemplate(res.invoice);
      setToast(t('invoice.savedSuccess'));
      setTimeout(() => setToast(null), 3000);
    } catch (err: any) {
      setError(err.message || t('invoice.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setLogoLoading(true);
    setError(null);
    try {
      const dataUrl = await resizeLogoToDataUrl(file, 600);
      setTemplate((prev) => ({ ...prev, logoDataUrl: dataUrl }));
    } catch (err: any) {
      setError(err.message || t('invoice.logoError'));
    } finally {
      setLogoLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveLogo = () => {
    setTemplate((prev) => ({ ...prev, logoDataUrl: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6 pb-16">
      {/* Toast */}
      {toast && (
        <div className="fixed top-5 end-5 z-50 rounded-xl bg-emerald-600 px-4 py-3 text-xs font-semibold text-white shadow-lg transition-all animate-bounce">
          ✓ {toast}
        </div>
      )}

      {/* Header bar */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-line pb-4 dark:border-slate-800">
        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => navigate('/store')}
            className="shrink-0"
          >
            <ChevronLeftIcon size={16} className={isRtl ? 'rotate-180' : ''} />
            <span className="ms-1">{t('common.back')}</span>
          </Button>
          <div>
            <h1 className="text-xl font-bold tracking-tight text-ink dark:text-white">
              {t('invoice.title')}
            </h1>
            <p className="text-xs text-ink-3 dark:text-slate-400">
              {t('invoice.subtitle')}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void fetchPreview(template)}
            loading={rendering}
          >
            {t('invoice.refreshPreview')}
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => void handleSave()}
            loading={saving}
            disabled={!isDirty || saving}
          >
            {t('common.save')}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-xs text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/40 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* Two Column Layout on Desktop, stacked on Mobile */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left: Form Controls */}
        <div className="space-y-6">
          {/* Section 1: Business Branding & Legal Identity */}
          <div className="rounded-xl border border-line bg-surface p-5 shadow-sm dark:border-slate-800 dark:bg-slate-850 space-y-4">
            <h2 className="text-sm font-bold text-ink dark:text-white">
              {t('invoice.sectionBusiness')}
            </h2>

            {/* Logo Upload Box */}
            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                {t('invoice.logo')}
              </label>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 rounded-lg border border-dashed border-line p-3 dark:border-slate-800 bg-surface-subtle dark:bg-slate-900/50">
                {template.logoDataUrl ? (
                  <div className="relative h-20 w-32 shrink-0 overflow-hidden rounded border border-line bg-white dark:border-slate-700 dark:bg-slate-900 flex items-center justify-center p-1">
                    <img
                      src={template.logoDataUrl}
                      alt="Logo preview"
                      className="max-h-full max-w-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="h-20 w-32 shrink-0 rounded border border-dashed border-slate-300 dark:border-slate-700 grid place-items-center text-xs text-slate-400">
                    {t('invoice.noLogo')}
                  </div>
                )}

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => fileInputRef.current?.click()}
                      loading={logoLoading}
                    >
                      {template.logoDataUrl ? t('invoice.changeLogo') : t('invoice.uploadLogo')}
                    </Button>
                    {template.logoDataUrl && (
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={handleRemoveLogo}
                      >
                        {t('common.remove')}
                      </Button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => void handleLogoUpload(e)}
                  />
                  <p className="text-[11px] text-ink-3 dark:text-slate-400 leading-tight">
                    {t('invoice.logoHint')}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                  {t('invoice.businessNameAr')}
                </label>
                <input
                  type="text"
                  dir="rtl"
                  maxLength={120}
                  value={template.businessNameAr}
                  onChange={(e) => setTemplate({ ...template, businessNameAr: e.target.value })}
                  placeholder="مثال: شركة النور للتجارة"
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                  {t('invoice.businessNameEn')}
                </label>
                <input
                  type="text"
                  dir="ltr"
                  maxLength={120}
                  value={template.businessNameEn}
                  onChange={(e) => setTemplate({ ...template, businessNameEn: e.target.value })}
                  placeholder={t('invoice.businessNamePlaceholder') || 'e.g. Al Noor Trading LLC'}
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                  {t('invoice.crNumber')}
                </label>
                <input
                  type="text"
                  maxLength={40}
                  value={template.cr}
                  onChange={(e) => setTemplate({ ...template, cr: e.target.value })}
                  placeholder="e.g. 1049281"
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                  {t('invoice.vatNumber')}
                </label>
                <input
                  type="text"
                  maxLength={40}
                  value={template.vatNumber}
                  onChange={(e) => setTemplate({ ...template, vatNumber: e.target.value })}
                  placeholder="e.g. OM10293847"
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                  {t('invoice.tel')}
                </label>
                <input
                  type="text"
                  maxLength={40}
                  value={template.tel}
                  onChange={(e) => setTemplate({ ...template, tel: e.target.value })}
                  placeholder="+968 9123 4567"
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                  {t('invoice.branch')}
                </label>
                <input
                  type="text"
                  maxLength={80}
                  value={template.branch}
                  onChange={(e) => setTemplate({ ...template, branch: e.target.value })}
                  placeholder={t('invoice.branchPlaceholder') || 'e.g. Muscat Main Branch'}
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>
          </div>

          {/* Section 2: Invoice Layout & Rates */}
          <div className="rounded-xl border border-line bg-surface p-5 shadow-sm dark:border-slate-800 dark:bg-slate-850 space-y-4">
            <h2 className="text-sm font-bold text-ink dark:text-white">
              {t('invoice.sectionFormat')}
            </h2>

            {/* Paper Size selector */}
            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1.5">
                {t('invoice.paper')}
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setTemplate({ ...template, paper: 'receipt80' })}
                  className={`flex items-center justify-center gap-2 rounded-lg border p-3 text-xs font-semibold transition ${
                    template.paper === 'receipt80'
                      ? 'border-brand bg-brand-50 text-brand dark:border-brand-tint dark:bg-brand-950/40 dark:text-brand-tint'
                      : 'border-line bg-surface text-ink-2 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-300'
                  }`}
                >
                  <span>🧾</span>
                  <span>{t('invoice.paperReceipt80')}</span>
                </button>

                <button
                  type="button"
                  onClick={() => setTemplate({ ...template, paper: 'a4' })}
                  className={`flex items-center justify-center gap-2 rounded-lg border p-3 text-xs font-semibold transition ${
                    template.paper === 'a4'
                      ? 'border-brand bg-brand-50 text-brand dark:border-brand-tint dark:bg-brand-950/40 dark:text-brand-tint'
                      : 'border-line bg-surface text-ink-2 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-800 dark:text-slate-300'
                  }`}
                >
                  <span>📄</span>
                  <span>{t('invoice.paperA4')}</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                  {t('invoice.titleAr')}
                </label>
                <input
                  type="text"
                  dir="rtl"
                  maxLength={80}
                  value={template.titleAr}
                  onChange={(e) => setTemplate({ ...template, titleAr: e.target.value })}
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                  {t('invoice.titleEn')}
                </label>
                <input
                  type="text"
                  dir="ltr"
                  maxLength={80}
                  value={template.titleEn}
                  onChange={(e) => setTemplate({ ...template, titleEn: e.target.value })}
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                  {t('invoice.vatRate')} (%)
                </label>
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={0.1}
                  value={template.vatRate}
                  onChange={(e) => setTemplate({ ...template, vatRate: Number(e.target.value) || 0 })}
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                  {t('invoice.referencePrefix')}
                </label>
                <input
                  type="text"
                  maxLength={20}
                  value={template.referencePrefix}
                  onChange={(e) => setTemplate({ ...template, referencePrefix: e.target.value })}
                  placeholder="e.g. sales"
                  className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                />
              </div>
            </div>

            <div className="flex items-center justify-between pt-2">
              <div>
                <span className="block text-xs font-semibold text-ink dark:text-white">
                  {t('invoice.showQr')}
                </span>
                <span className="text-[11px] text-ink-3 dark:text-slate-400">
                  {t('invoice.showQrDesc')}
                </span>
              </div>
              <Toggle
                on={template.showQr}
                onChange={(val: boolean) => setTemplate({ ...template, showQr: val })}
                label={t('invoice.showQr')}
              />
            </div>
          </div>

          {/* Section 3: Policy Footer & Thanks Note */}
          <div className="rounded-xl border border-line bg-surface p-5 shadow-sm dark:border-slate-800 dark:bg-slate-850 space-y-4">
            <h2 className="text-sm font-bold text-ink dark:text-white">
              {t('invoice.sectionFooter')}
            </h2>

            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                {t('invoice.thanksAr')}
              </label>
              <input
                type="text"
                dir="rtl"
                maxLength={120}
                value={template.thanksAr}
                onChange={(e) => setTemplate({ ...template, thanksAr: e.target.value })}
                className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-ink-2 dark:text-slate-300 mb-1">
                {t('invoice.footerAr')}
              </label>
              <textarea
                dir="rtl"
                rows={4}
                maxLength={1500}
                value={template.footerAr}
                onChange={(e) => setTemplate({ ...template, footerAr: e.target.value })}
                placeholder="سياسة الاستبدال والاسترجاع: يمكن استبدال البضائع خلال 14 يوماً من تاريخ الشراء بشرط وجود الفاتورة الأصلية..."
                className="w-full rounded-lg border border-line bg-surface px-3 py-2 text-xs text-ink outline-none focus:border-brand dark:border-slate-700 dark:bg-slate-800 dark:text-white"
              />
              <span className="mt-1 block text-end text-[10px] text-ink-4 dark:text-slate-500">
                {template.footerAr.length} / 1500
              </span>
            </div>
          </div>
        </div>

        {/* Right: Sticky Live PDF Preview */}
        <div className="lg:sticky lg:top-6 flex flex-col h-[calc(100vh-8rem)] min-h-[500px]">
          <div className="rounded-xl border border-line bg-surface p-4 shadow-sm dark:border-slate-800 dark:bg-slate-850 flex flex-col flex-1 min-h-0">
            <div className="flex items-center justify-between pb-3 border-b border-line dark:border-slate-800">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-ink dark:text-white">
                  {t('invoice.livePreview')}
                </span>
                {template.paper === 'receipt80' ? (
                  <span className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
                    80mm Thermal
                  </span>
                ) : (
                  <span className="rounded bg-blue-100 px-2 py-0.5 text-[10px] font-bold text-blue-800 dark:bg-blue-950/40 dark:text-blue-300">
                    A4 PDF
                  </span>
                )}
              </div>

              {rendering && (
                <div className="flex items-center gap-1.5 text-[11px] text-brand">
                  <div className="h-3 w-3 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                  <span>{t('invoice.rendering')}</span>
                </div>
              )}
            </div>

            {previewError && (
              <div className="mt-2 rounded-lg bg-amber-50 p-2 text-[11px] text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
                ⚠️ {previewError} ({t('invoice.retainingPrevious')})
              </div>
            )}

            {/* Preview Frame */}
            <div className="relative mt-3 flex-1 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 dark:border-slate-800 dark:bg-slate-900">
              {pdfBlobUrl ? (
                <iframe
                  src={`${pdfBlobUrl}#toolbar=0&navpanes=0`}
                  title="Invoice PDF Preview"
                  className="h-full w-full border-0"
                />
              ) : (
                <div className="grid h-full place-items-center p-8 text-center text-xs text-ink-3 dark:text-slate-400">
                  {rendering ? (
                    <div className="space-y-2">
                      <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                      <p>{t('invoice.generatingFirstPreview')}</p>
                    </div>
                  ) : (
                    <p>{t('invoice.noPreview')}</p>
                  )}
                </div>
              )}

              {/* Rendering overlay */}
              {rendering && pdfBlobUrl && (
                <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] dark:bg-black/40 flex items-center justify-center transition-opacity">
                  <div className="rounded-xl bg-white/90 px-4 py-2 text-xs font-semibold text-ink shadow-md dark:bg-slate-800 dark:text-white flex items-center gap-2">
                    <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-brand border-t-transparent" />
                    <span>{t('invoice.rendering')}…</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
